import { supabase } from '@/services/supabase';
import type { PropertyShare, CreateShareOptions, ShareExpiration } from '@/types/share';

// ── Helpers ───────────────────────────────────────────────────────────────────

function expirationToDays(exp: ShareExpiration): number | null {
  if (exp === 'never') return null;
  if (exp === '7d') return 7;
  if (exp === '30d') return 30;
  return 90;
}

function buildExpiresAt(expiration: ShareExpiration): string | null {
  const days = expirationToDays(expiration);
  if (days === null) return null;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

async function hashPassword(password: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(password));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function rowToShare(row: Record<string, unknown>): PropertyShare {
  return {
    id: row.id as string,
    propertyId: row.property_id as string,
    userId: row.user_id as string,
    token: row.token as string,
    label: (row.label as string) ?? '',
    passwordHash: (row.password_hash as string | null) ?? null,
    includeScouting: (row.include_scouting as boolean) ?? true,
    includePhotos: (row.include_photos as boolean) ?? false,
    expiresAt: (row.expires_at as string | null) ?? null,
    lastAccessedAt: (row.last_accessed_at as string | null) ?? null,
    accessCount: (row.access_count as number) ?? 0,
    active: (row.active as boolean) ?? true,
    createdAt: row.created_at as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export class ShareService {
  async createShare(propertyId: string, options: CreateShareOptions = {}): Promise<PropertyShare> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const token = crypto.randomUUID();
    const trimmedPassword = options.password?.trim() ?? '';
    const passwordHash = trimmedPassword.length > 0 ? await hashPassword(trimmedPassword) : null;
    const expiresAt = buildExpiresAt(options.expiration ?? '30d');

    const { data, error } = await supabase
      .from('property_shares')
      .insert({
        property_id: propertyId,
        user_id: user.id,
        token,
        label: options.label ?? '',
        password_hash: passwordHash,
        include_scouting: options.includeScouting ?? true,
        include_photos: options.includePhotos ?? false,
        expires_at: expiresAt,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw new Error(`Failed to create share: ${error.message}`);
    return rowToShare(data as Record<string, unknown>);
  }

  async getSharesForProperty(propertyId: string): Promise<PropertyShare[]> {
    const { data, error } = await supabase
      .from('property_shares')
      .select('*')
      .eq('property_id', propertyId)
      .eq('active', true)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to load shares: ${error.message}`);
    return ((data ?? []) as Record<string, unknown>[]).map(rowToShare);
  }

  async revokeShare(shareId: string): Promise<void> {
    const { error } = await supabase
      .from('property_shares')
      .update({ active: false })
      .eq('id', shareId);

    if (error) throw new Error(`Failed to revoke share: ${error.message}`);
  }

  async deleteShare(shareId: string): Promise<void> {
    const { error } = await supabase
      .from('property_shares')
      .delete()
      .eq('id', shareId);

    if (error) throw new Error(`Failed to delete share: ${error.message}`);
  }

  getShareUrl(token: string): string {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'https://ridgen.app';
    return `${base}/shared/${token}`;
  }
}

export const shareService = new ShareService();
